import { showToast } from "@mux/ui/toast"
import { localValues } from "@/storage/browser"
import { readUserSessionToken } from "@/storage/user-session"
import { getNullableStringPreference, setPreference } from "@/transport/preferences"
import { onAccountPreferencesFocus, onAccountPreferencesInvalidated } from "@/context/account-preference"
import { describeError } from "@/utils/error-message"
import type { ColorScheme, ThemePersistence } from "@mux/ui/theme"

// The theme is an account setting, so every device shows the same one. This
// device keeps the last theme it showed, and its CSS, only to paint it before
// the account loads. Light or dark mode stays this device's choice.
const accountTheme = "/api/ai/preferences/theme"

const keys = {
  theme: "mux.theme.id",
  colorScheme: "mux.theme.color-scheme",
  lightCss: "mux.theme.css.light",
  darkCss: "mux.theme.css.dark",
} as const

function colorScheme(value: string | null): ColorScheme | undefined {
  if (value === "light" || value === "dark" || value === "system") return value
}

// Saves run one at a time, in order, and skip a pick a later one replaced
// while it waited, so the account ends on this device's last pick. A pick
// belongs to the account signed in when it was made; it is dropped if another
// account signs in before its turn.
let queue = Promise.resolve()
// A load that overlaps a save on this device may return the theme the save
// replaces, so loads apply only when no save started or ran meanwhile.
let saves = 0
let revision = 0

export const themePersistence: ThemePersistence = {
  load() {
    return {
      themeId: localValues.getItem(keys.theme) ?? undefined,
      colorScheme: colorScheme(localValues.getItem(keys.colorScheme)),
    }
  },
  saveTheme(themeId) {
    localValues.setItem(keys.theme, themeId)
    saves++
    const pick = ++revision
    const owner = readUserSessionToken()
    queue = queue.then(() => {
      if (pick === revision && owner === readUserSessionToken()) return setPreference(accountTheme, themeId)
    }).catch((error) => {
      showToast({
        title: "Could not save the theme to your account",
        description: describeError(error),
      })
    }).finally(() => { saves-- })
  },
  saveColorScheme(scheme) {
    localValues.setItem(keys.colorScheme, scheme)
  },
  cacheCss(mode, css) {
    localValues.setItem(mode === "dark" ? keys.darkCss : keys.lightCss, css)
  },
  clearCss() {
    localValues.removeItem(keys.lightCss)
    localValues.removeItem(keys.darkCss)
  },
  subscribe(listener) {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === keys.theme && event.newValue) listener({ themeId: event.newValue })
      if (event.key === keys.colorScheme) {
        const scheme = colorScheme(event.newValue)
        if (scheme) listener({ colorScheme: scheme })
      }
    }
    const sync = () => {
      if (saves) return
      const started = revision
      void getNullableStringPreference(accountTheme).then((themeId) => {
        // null: the account has not picked a theme, so this device keeps its own.
        if (!themeId || saves || started !== revision || themeId === localValues.getItem(keys.theme)) return
        localValues.setItem(keys.theme, themeId)
        listener({ themeId })
      }, () => {
        // Signed out or offline: keep showing this device's last theme.
      })
    }
    window.addEventListener("storage", handleStorage)
    const stopFocus = onAccountPreferencesFocus(sync)
    const stopInvalidation = onAccountPreferencesInvalidated(sync)
    sync()
    return () => {
      window.removeEventListener("storage", handleStorage)
      stopFocus()
      stopInvalidation()
    }
  },
}
